<template>
  <div>
    <ExpandableItemCard
      :title="item?.name || defaultTitle"
      :subtitle="item?.amount ? `(${item?.amount}${item?.unit ? ' ' + item?.unit : ''})` : ''"
      item-label="item"
      :index="index"
      :total-items="totalItems"
      :start-expanded="startExpanded"
      :expanded="expanded"
      :active="active"
      :drag-enabled="dragEnabled"
      :has-warning="
        showLowDvWarning || sodiumExceedsLimit || !!missingAmountWarning || !!dvMatchWarning
      "
      margin-bottom="mb-2"
      @toggle="$emit('toggle')"
    >
      <template #header-title>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="formattedHeaderTitle"></span>
      </template>
      <template #header-actions>
        <button
          type="button"
          class="delete-header-btn"
          aria-label="Remove this ingredient"
          @click.stop="handleRemove"
        >
          <i class="fas fa-trash" aria-hidden="true"></i>
        </button>
      </template>
      <template #header-subtitle>
        <AmountWithUnitInput
          :amount="item?.amount"
          :unit="item?.unit"
          :uid="item?._uid"
          :locked-unit="isUnitLocked"
          :default-unit="defaultUnit"
          :hide-unit="isCalories"
          :allowed-units="currentAllowedUnits"
          class="is-small header-amount-input"
          placeholder="Amt"
          @update:amount="update({ amount: $event })"
          @update:unit="updateAndSave({ unit: $event })"
          @blur="triggerSave"
          @click.stop
        />
      </template>
      <div v-if="isDvLine" class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label class="label is-small mb-0 mr-2 label-inline">Nutrient</label>
          <div class="control is-flex-grow-1">
            <NutrientCombobox
              :key="`nutrient-${item?._uid}`"
              :model-value="item?.dv_key"
              label-key="label"
              value-key="value"
              group-key="group"
              placeholder="Search nutrients..."
              :exclude-values="otherSelectedDvKeys"
              aria-label="FDA Nutrients"
              @update:model-value="handleDvKeyChange"
              @clear="handleDvKeyChange('')"
              @invalid-input="handleInvalidDvInput"
            />
          </div>
        </div>
        <div class="checkbox-group mt-1 content-indent">
          <label v-if="hasParens" class="checkbox is-size-7 is-flex is-align-items-center">
            <input
              type="checkbox"
              class="mr-1"
              :checked="item?.italicize_parenthetical"
              @change="updateAndSave({ italicize_parenthetical: $event.target.checked })"
            />
            <span>{{ italicizeLabel }}</span>
          </label>
          <label class="checkbox is-size-7 is-flex is-align-items-center">
            <input
              type="checkbox"
              class="mr-1"
              :checked="hasFootnote"
              @change="toggleFootnote($event.target.checked)"
            />
            <span>Footnote</span>
          </label>
        </div>
        <FootnoteItemEditor
          :item="item"
          class="content-indent"
          bordered
          @update="updateAndSave($event)"
        />
      </div>

      <div v-else class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label
            :for="`ingredient-name-${item?._uid}`"
            class="label is-small mb-0 mr-2 label-inline"
          >
            Name
            <IconHint :content="HINT_TEXTS.CUSTOM_FORMATTING" />
          </label>
          <div class="control is-flex-grow-1">
            <input
              :id="`ingredient-name-${item?._uid}`"
              :key="`name-${item?._uid}`"
              type="text"
              class="input is-small"
              :value="item?.name"
              name="ingredient"
              placeholder="e.g., CoQ10"
              @input="update({ name: $event.target.value })"
              @blur="triggerSave"
            />
          </div>
          <ExcipientWarningHint v-if="excipientWarning" :warning="excipientWarning" class="ml-2" />
        </div>
        <div class="checkbox-group mt-1 content-indent">
          <label v-if="hasParens" class="checkbox is-size-7 is-flex is-align-items-center">
            <input
              type="checkbox"
              class="mr-1"
              :checked="item?.italicize_parenthetical"
              @change="updateAndSave({ italicize_parenthetical: $event.target.checked })"
            />
            <span>{{ italicizeLabel }}</span>
          </label>
          <ExtraTextField
            :model-value="item?.extra_text"
            :is-expanded="hasExtraText || showExtraInput"
            placeholder="(e.g., 10 billion CFUs)"
            checkbox-test-id="ingredient-extra-text-checkbox"
            input-test-id="ingredient-extra-text-input"
            @toggle="toggleExtraText"
            @update:model-value="updateAndSave({ extra_text: $event })"
          />
          <label class="checkbox is-size-7 is-flex is-align-items-center">
            <input
              type="checkbox"
              class="mr-1"
              :checked="hasFootnote"
              @change="toggleFootnote($event.target.checked)"
            />
            <span>Footnote</span>
          </label>
        </div>

        <FootnoteItemEditor
          :item="item"
          class="content-indent"
          bordered
          @update="updateAndSave($event)"
        />
      </div>

      <p
        v-if="dvMatchWarning"
        class="notification is-warning is-light bordered-left py-2 px-3 mb-2"
      >
        <i class="fas fa-exclamation-triangle mr-2"></i>
        {{ dvMatchWarning.message }}
      </p>

      <div class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label
            :for="`ingredient-source-${item?._uid}`"
            class="label is-small mb-0 mr-2 label-inline pt-1"
          >
            Source
            <IconHint :content="HINT_TEXTS.CUSTOM_FORMATTING" />
          </label>
          <div class="control is-flex-grow-1">
            <input
              :id="`ingredient-source-${item?._uid}`"
              :key="`source-${item?._uid}`"
              type="text"
              class="input is-small"
              :value="item?.source"
              name="source"
              placeholder="e.g., as Ascorbic Acid"
              @input="update({ source: $event.target.value })"
              @blur="triggerSave"
            />
          </div>
        </div>
      </div>

      <div class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label
            :for="`ingredient-amount-${item?._uid}`"
            class="label is-small mb-0 mr-2 label-inline pt-1"
            >Amount</label
          >
          <div class="control is-expanded">
            <AmountWithUnitInput
              :input-id="`ingredient-amount-${item?._uid}`"
              :amount="item?.amount"
              :unit="item?.unit"
              :uid="item?._uid"
              :locked-unit="isUnitLocked"
              :default-unit="defaultUnit"
              :hide-unit="isCalories"
              :allowed-units="currentAllowedUnits"
              @update:amount="update({ amount: $event })"
              @update:unit="updateAndSave({ unit: $event })"
              @blur="triggerSave"
            />
          </div>
        </div>
        <p
          v-if="showLowDvWarning"
          class="notification is-danger is-light bordered-left py-2 px-3 mt-2"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i><strong>FDA Compliance:</strong>
          {{ lowDvWarningText }}
        </p>
        <p
          v-if="sodiumExceedsLimit"
          class="notification is-danger is-light bordered-left py-2 px-3 mt-2"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i><strong>FDA Compliance:</strong>
          Sodium exceeds 2300mg daily limit.
        </p>
        <p
          v-if="missingAmountWarning"
          class="notification is-warning is-light bordered-left py-2 px-3 mt-2"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i>
          {{ missingAmountWarning }}
        </p>
      </div>
    </ExpandableItemCard>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Remove Ingredient"
      message="Are you sure you want to remove this ingredient?"
      confirm-text="Remove"
      :show-dont-ask-again="true"
      @confirm="doRemove"
      @dont-ask-again="setSkipDeleteConfirm"
    />
  </div>
</template>

<script setup>
  import { computed, toRef, inject, watch } from "vue";
  import { useListItem } from "./composables/use-list-item";
  import ExpandableItemCard from "./expandable-item-card.vue";
  import NutrientCombobox from "./nutrient-combobox.vue";
  import AmountWithUnitInput from "./amount-with-unit-input.vue";
  import ConfirmDialog from "../shared/confirm-dialog.vue";
  import { formatLabelName, hasParentheses, isLatinParenthetical } from "./utils/formatting";
  import FootnoteItemEditor from "./footnote-item-editor.vue";
  import { useItemRemove } from "./composables/use-item-remove";
  import { useFootnoteExtraText } from "./composables/use-footnote-extra-text";
  import { useFormattingLabels } from "./composables/use-formatting-labels";
  import { INGREDIENT_UNITS, HINT_TEXTS } from "./utils/constants";
  import { scrollToSourceAfterSort } from "./utils/focus-helpers";
  import IconHint from "../icon-hint.vue";
  import ExtraTextField from "./extra-text-field.vue";
  import ExcipientWarningHint from "./excipient-warning-hint.vue";

  const props = defineProps({
    uid: { type: Number, required: true },
    isDvLine: { type: Boolean, default: false },
    startExpanded: { type: Boolean, default: false },
    dragEnabled: { type: Boolean, default: false },
    expanded: { type: Boolean, default: undefined },
    active: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
  });

  const emit = defineEmits(["removed", "toggle", "invalid-dv-input"]);
  const triggerSave = inject("triggerSave");

  const listName = computed(() => (props.isDvLine ? "dvLines" : "lines"));
  const {
    store,
    item,
    index,
    totalItems,
    update: storeUpdate,
    remove,
  } = useListItem(listName.value, toRef(props, "uid"));

  function update(updates) {
    storeUpdate(updates);
  }

  function updateAndSave(updates) {
    storeUpdate(updates);
    triggerSave?.();
  }

  const defaultTitle = computed(() => {
    return props.isDvLine ? "Select Nutrient" : "New Ingredient";
  });

  const formattedHeaderTitle = computed(() => {
    const name = item.value?.name || defaultTitle.value;
    return formatLabelName(name, {
      italicizeParenthetical: item.value?.italicize_parenthetical,
      disableSubscripts: !props.isDvLine,
    });
  });

  const otherSelectedDvKeys = computed(() => {
    return store.dvLines
      .filter((_, idx) => idx !== index.value)
      .map((line) => line.dv_key)
      .filter(Boolean);
  });

  const defaultUnit = computed(() => {
    const nutrient = store.fdaNutrientsMap[item.value?.dv_key];
    return nutrient?.unit || "mg";
  });

  const isUnitLocked = computed(() => {
    return !!(props.isDvLine && item.value?.dv_key && store.fdaNutrientsMap[item.value.dv_key]);
  });

  const currentAllowedUnits = computed(() => {
    if (isUnitLocked.value) {
      return [defaultUnit.value];
    }
    return INGREDIENT_UNITS;
  });

  const isCalories = computed(() => {
    if (props.isDvLine) {
      return item.value?.dv_key === "calories";
    }
    return item.value?.name?.toLowerCase() === "calories";
  });

  const { hasParens, italicizeLabel } = useFormattingLabels(item);

  // Auto-enable italicize when Latin parenthetical is detected
  watch(
    () => item.value?.name,
    (newName, oldName) => {
      if (newName !== oldName && hasParentheses(newName) && isLatinParenthetical(newName)) {
        if (!item.value?.italicize_parenthetical) {
          update({ italicize_parenthetical: true });
        }
      }
    }
  );

  const percentDV = computed(() => {
    if (!props.isDvLine || !item.value?.dv_key || !item.value?.amount) return null;
    const nutrient = store.fdaNutrientsMap[item.value.dv_key];
    if (!nutrient?.dv) return null;
    const amount = parseFloat(item.value.amount);
    if (isNaN(amount) || amount <= 0) return null;
    return (amount / nutrient.dv) * 100;
  });

  const isSodiumBelowThreshold = computed(() => {
    if (!props.isDvLine || item.value?.dv_key !== "sodium") return false;
    const rawAmount = item.value?.amount;
    if (rawAmount === "" || rawAmount === null || rawAmount === undefined) return false;
    const amount = parseFloat(rawAmount);
    if (isNaN(amount)) return false;
    return amount < 5;
  });

  const sodiumExceedsLimit = computed(() => {
    if (!props.isDvLine || item.value?.dv_key !== "sodium") return false;
    return store.dvWarnings.some((w) => w.toLowerCase().includes("sodium exceeds"));
  });

  const showLowDvWarning = computed(() => {
    if (!props.isDvLine || !item.value?.dv_key) return false;
    if (props.editing) return false;

    // Sodium: special <5mg absolute threshold (21 CFR 101.9(c)(4))
    if (item.value.dv_key === "sodium") return isSodiumBelowThreshold.value;

    const nutrient = store.fdaNutrientsMap[item.value.dv_key];
    if (!nutrient?.dv) return false;

    const rawAmount = item.value?.amount;
    if (rawAmount === "" || rawAmount === null || rawAmount === undefined) return false;

    const amount = parseFloat(rawAmount);
    if (isNaN(amount)) return false;
    if (amount === 0) return true;

    return percentDV.value !== null && percentDV.value < 2;
  });

  const LOW_DV_SUFFIX =
    "will be hidden from the Supplement Facts panel. Ensure the source ingredient is listed in the ingredient statement below the supplement facts panel. The ingredients must be listed in descending weight order.";

  const lowDvWarningText = computed(() => {
    if (isSodiumBelowThreshold.value) {
      return `Sodium below 5mg is considered zero per FDA regulations and ${LOW_DV_SUFFIX}`;
    }
    return `Vitamins/minerals below 2% DV cannot be declared on supplement labels and ${LOW_DV_SUFFIX}`;
  });

  // Warning for non-DV lines that appear to be excipients (non-dietary ingredients)
  const excipientWarning = computed(() => {
    if (props.isDvLine) return null;
    const warnings = store.calculatedValues?.line_warnings;
    if (!warnings) return null;
    return warnings[index.value] || null;
  });

  const dvMatchWarning = computed(() => {
    if (props.isDvLine) return null;
    const warnings = store.calculatedValues?.dv_match_warnings;
    if (!warnings) return null;
    return warnings[index.value] || null;
  });

  const missingAmountWarning = computed(() => {
    if (props.editing) return null;
    if (item.value?.amount) return null;
    const section = props.isDvLine ? "dv" : "lines";
    return store.validationWarnings.missing_amounts?.[section]?.[index.value] || null;
  });

  function handleDvKeyChange(dvKey) {
    const nutrient = store.fdaNutrientsMap[dvKey];
    const success = store.updateDvLineKey(props.uid, dvKey, nutrient);
    if (success && dvKey) {
      scrollToSourceAfterSort(props.uid);
    }
  }

  function handleInvalidDvInput(invalidText) {
    emit("invalid-dv-input", { uid: props.uid, invalidText });
  }

  const { showDeleteConfirm, handleRemove, doRemove, setSkipDeleteConfirm } = useItemRemove({
    cookieName: "skip_ingredient_delete_1h",
    remove,
    triggerSave,
    onRemoved: (index) => emit("removed", index),
  });

  const {
    hasFootnote,
    hasExtraText,
    showExtraText: showExtraInput,
    toggleFootnote,
    toggleExtraText,
  } = useFootnoteExtraText(item, update, { clearExtraTextValue: null, onSave: triggerSave });
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";
</style>
